var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOkapiKy } from "@folio/stripes/core";
import debounce from "lodash/debounce";
import { useQuery } from "react-query";
import { fetchPersonSearchOptions, } from "../../api/personService";
import { PersonLegalFormEnum } from "../../types/Api";
var MIN_QUERY_LENGTH = 3;
var PAGE_LIMIT = 20;
var BIO_NOTE_MAX_LENGTH = 60;
export var isNaturalPerson = function (person) {
    return person.legalForm === PersonLegalFormEnum.NATURAL;
};
export var isLegalPerson = function (person) {
    return person.legalForm === PersonLegalFormEnum.LEGAL;
};
var truncateBioNote = function (note) {
    return note.length > BIO_NOTE_MAX_LENGTH
        ? "".concat(note.slice(0, BIO_NOTE_MAX_LENGTH).trimEnd(), "\u2026")
        : note;
};
// NK headings mark the non-sorting part as "<<z >>Lobkowicz"; show it as "z Lobkowicz"
var stripNonSortingMarkers = function (value) {
    return (value !== null && value !== void 0 ? value : "").replace(/<<|>>/g, "").replace(/\s+/g, " ").trim();
};
var withSourceId = function (label, person) {
    var _a;
    var sourceId = (_a = person.sourceId) === null || _a === void 0 ? void 0 : _a.trim();
    return sourceId ? "".concat(label, " [").concat(sourceId, "]").trim() : label;
};
export var buildPersonOptionLabel = function (person) {
    var _a, _b, _c;
    if (!person)
        return "";
    if (isNaturalPerson(person)) {
        var label = "".concat(stripNonSortingMarkers(person.firstName), " ").concat(stripNonSortingMarkers(person.lastName)).trim();
        if (person.birthDate || person.deathDate) {
            label += " (".concat((_a = person.birthDate) !== null && _a !== void 0 ? _a : "?", "\u2013").concat((_b = person.deathDate) !== null && _b !== void 0 ? _b : "", ")");
        }
        if ((_c = person.biographicalNote) === null || _c === void 0 ? void 0 : _c.trim()) {
            label += " \u2014 ".concat(truncateBioNote(person.biographicalNote.trim()));
        }
        return withSourceId(label, person);
    }
    if (isLegalPerson(person)) {
        return withSourceId(stripNonSortingMarkers(person.name), person);
    }
    return "";
};
export var usePersonOptions = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.linkedOnly, linkedOnly = _c === void 0 ? false : _c;
    var okapiKy = useOkapiKy();
    var _d = useState({ name: "" }), filterParams = _d[0], setFilterParams = _d[1];
    var _e = useState([]), manualOptions = _e[0], setManualOptions = _e[1];
    var personCacheRef = useRef(new Map());
    var _f = useState(""), pendingName = _f[0], setPendingName = _f[1];
    var isEnabled = filterParams.name.length >= MIN_QUERY_LENGTH;
    var _g = useQuery([
        "personSearchOptions",
        filterParams.name,
        filterParams.legalForm,
        linkedOnly,
    ], 
    // consuming the signal lets react-query abort the browser request of a
    // search the user has typed past (DB-902); the backend is not interrupted
    function (_a) {
        var signal = _a.signal;
        return fetchPersonSearchOptions(okapiKy, __assign({ nameFilter: filterParams.name, legalForm: filterParams.legalForm, 
            // same query also matches the NK authority id (OR-combined on the backend)
            sourceIdFilter: filterParams.name, limit: PAGE_LIMIT }, (linkedOnly ? { linkedOnly: true } : {})), signal);
    }, { enabled: isEnabled }), _h = _g.data, personSearchData = _h === void 0 ? [] : _h, isFetching = _g.isFetching;
    var personOptionsLoading = pendingName.length >= MIN_QUERY_LENGTH &&
        (pendingName !== filterParams.name || isFetching);
    useEffect(function () {
        personSearchData.forEach(function (person) {
            return personCacheRef.current.set(person.id, person);
        });
    }, [personSearchData]);
    var personSelectOptions = useMemo(function () {
        if (isEnabled) {
            return personSearchData.map(function (person) { return ({
                value: person.id,
                label: buildPersonOptionLabel(person) || person.id,
            }); });
        }
        return filterParams.name === "" ? manualOptions : [];
    }, [isEnabled, personSearchData, manualOptions, filterParams.name]);
    var debouncedSetFilter = useMemo(function () {
        return debounce(function (name, legalForm) {
            setFilterParams({ name: name, legalForm: legalForm });
        }, 300);
    }, []);
    useEffect(function () {
        return function () {
            debouncedSetFilter.cancel();
        };
    }, [debouncedSetFilter]);
    var handlePersonFilter = useCallback(function (name, legalForm) {
        var query = name.trim();
        setPendingName(query);
        debouncedSetFilter(query, legalForm);
    }, [debouncedSetFilter]);
    var clearPersonFilter = useCallback(function () {
        debouncedSetFilter.cancel();
        setPendingName("");
        setFilterParams({ name: "" });
    }, [debouncedSetFilter]);
    var findPersonById = function (id) {
        return personCacheRef.current.get(id);
    };
    // The raw results of the current search; unlike findPersonById (a cache
    // filled after render) they are there in the same render as personOptions.
    var personSearchResults = useMemo(function () { return (isEnabled ? personSearchData : []); }, [isEnabled, personSearchData]);
    return {
        findPersonById: findPersonById,
        personSearchResults: personSearchResults,
        personOptions: personSelectOptions,
        setPersonOptions: setManualOptions,
        onPersonFilter: handlePersonFilter,
        clearPersonFilter: clearPersonFilter,
        personOptionsLoading: personOptionsLoading,
    };
};
